import React, { useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useSnackbar } from "notistack";
import {
  deleteTrackerAutomationAttachmentApi,
  uploadTrackerAutomationAttachmentApi,
} from "../../../DAL/trackerAutomation/trackerAutomationAttachment";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_LIMITS,
  formatBytes,
  getAttachmentKind,
  planAttachmentDelivery,
  validateAttachmentFile,
} from "./emailContentUtils";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const KIND_ICONS = {
  image: ImageOutlinedIcon,
  pdf: PictureAsPdfOutlinedIcon,
  audio: AudiotrackOutlinedIcon,
  video: MovieOutlinedIcon,
};

/**
 * Delete files that were saved on the workflow before editing but are no
 * longer on it after a successful save. Returns the number of failures.
 */
export const deleteDetachedAttachments = async (before = [], after = []) => {
  const keep = new Set((after || []).map((item) => item?.id).filter(Boolean));
  const removed = (before || []).filter(
    (item) => item?.id && !keep.has(item.id)
  );
  const results = await Promise.all(
    removed.map((item) =>
      deleteTrackerAutomationAttachmentApi(item.id).catch(() => null)
    )
  );
  return results.filter((res) => res?.code !== 200).length;
};

/**
 * Email attachments for an automation workflow.
 * Files uploaded in this session are deleted immediately on remove; files
 * already saved on the workflow are only detached here and deleted by the
 * panel after the workflow is saved (so Cancel never breaks a saved email).
 */
export default function AttachmentPicker({
  attachments = [],
  onChange,
  trackerId,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef(null);
  const sessionUploadsRef = useRef(new Set());
  const [uploadingName, setUploadingName] = useState("");
  const [removingId, setRemovingId] = useState("");

  const list = Array.isArray(attachments) ? attachments : [];
  const { items, attachedBytes } = planAttachmentDelivery(list);
  const busy = Boolean(uploadingName || removingId);
  const atLimit = list.length >= ATTACHMENT_LIMITS.maxFiles;

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    if (!trackerId) {
      enqueueSnackbar("Save the automation to a tracker before attaching files", {
        variant: "error",
      });
      return;
    }

    let current = list;
    for (const file of files) {
      const error = validateAttachmentFile(file, current);
      if (error) {
        enqueueSnackbar(error, { variant: "error" });
        continue;
      }
      setUploadingName(file.name);
      const res = await uploadTrackerAutomationAttachmentApi({
        file,
        trackerId,
      });
      if (res?.code === 200 && res.attachment?.id) {
        sessionUploadsRef.current.add(res.attachment.id);
        current = [...current, res.attachment];
        onChange(current);
      } else {
        enqueueSnackbar(res?.message || `Could not upload "${file.name}"`, {
          variant: "error",
        });
      }
    }
    setUploadingName("");
  };

  const handleRemove = async (item) => {
    const next = list.filter((entry) => entry.id !== item.id);
    if (!sessionUploadsRef.current.has(item.id)) {
      onChange(next);
      return;
    }
    setRemovingId(item.id);
    const res = await deleteTrackerAutomationAttachmentApi(item.id);
    setRemovingId("");
    if (res?.code !== 200) {
      enqueueSnackbar(res?.message || "Could not remove attachment", {
        variant: "error",
      });
      return;
    }
    sessionUploadsRef.current.delete(item.id);
    onChange(next);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: MUTED,
            textTransform: "uppercase",
            letterSpacing: 0.35,
          }}
        >
          Attachments ({list.length}/{ATTACHMENT_LIMITS.maxFiles})
        </Typography>
        <input
          ref={inputRef}
          type="file"
          accept={ATTACHMENT_ACCEPT}
          multiple
          hidden
          onChange={handleFiles}
        />
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={() => inputRef.current?.click()}
          disabled={busy || atLimit}
          style={{ opacity: busy || atLimit ? 0.6 : 1 }}
        >
          <AttachFileIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "-3px" }} />
          Attach files
        </button>
      </Box>

      {items.length || uploadingName ? (
        <Box
          sx={{
            border: `1px solid ${BORDER}`,
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          {items.map((item) => {
            const Icon =
              KIND_ICONS[item.kind || getAttachmentKind(item.mimeType)] ||
              InsertDriveFileOutlinedIcon;
            const isLink = item.effectiveDeliverAs === "link";
            return (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  borderBottom: `1px solid ${BORDER}`,
                  "&:last-of-type": { borderBottom: 0 },
                }}
              >
                <Icon sx={{ fontSize: 20, color: MUTED, flexShrink: 0 }} />
                <Typography
                  title={item.name}
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#0f172a",
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: MUTED, flexShrink: 0 }}>
                  {formatBytes(item.size)}
                </Typography>
                <Chip
                  size="small"
                  label={isLink ? "Sent as link" : "Attached"}
                  color={isLink ? "default" : "success"}
                  variant="outlined"
                  sx={{ fontSize: 11, height: 22, flexShrink: 0 }}
                />
                <Tooltip title="Remove">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Remove ${item.name}`}
                      onClick={() => handleRemove(item)}
                      disabled={busy}
                    >
                      {removingId === item.id ? (
                        <CircularProgress size={14} />
                      ) : (
                        <CloseIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
          {uploadingName ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.9,
              }}
            >
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                Uploading {uploadingName}…
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}

      <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.75 }}>
        {formatBytes(attachedBytes)} of{" "}
        {formatBytes(ATTACHMENT_LIMITS.maxTotalAttachBytes)} attached · Images,
        PDF, audio or video are sent as email attachments (up to{" "}
        {formatBytes(ATTACHMENT_LIMITS.maxAttachBytes)} each /{" "}
        {formatBytes(ATTACHMENT_LIMITS.maxTotalAttachBytes)} total). Upload
        limit {formatBytes(ATTACHMENT_LIMITS.maxUploadBytes)}; only overflow is
        sent as a link.
      </Typography>
    </Box>
  );
}
